import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback } from "react";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import { getTasksQueryKey } from "~/pages/dashboard/task-manager/task-dialog/task-constants";
import type { TaskManagerDTO } from "~/types/api-custom";

type DeleteTaskVariables = {
  taskId: number;
};

type DeleteTaskContext = {
  previousTaskQueries: [QueryKey, TaskManagerDTO[] | undefined][];
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const tasksQueryKey = getTasksQueryKey();

  const { mutateAsync: mutateTask, isPending } = useMutation({
    mutationFn: async ({ taskId }: DeleteTaskVariables) => {
      const response = await fileApi.client.DELETE("/api/Task/{taskId}", {
        params: { path: { taskId } },
      });

      if (response.error) {
        const error = getErrorMessage(response.error, "Failed to delete task");
        throw new Error(error);
      }
    },
    onMutate: async ({ taskId }) => {
      await queryClient.cancelQueries({ queryKey: tasksQueryKey });
      const previousTaskQueries = queryClient.getQueriesData<TaskManagerDTO[]>({
        queryKey: tasksQueryKey,
      });

      queryClient.setQueriesData<TaskManagerDTO[]>({ queryKey: tasksQueryKey }, (old) =>
        Array.isArray(old) ? old.filter((task) => task.id !== taskId) : old
      );

      return { previousTaskQueries } satisfies DeleteTaskContext;
    },
    onError: (err, _variables, context) => {
      context?.previousTaskQueries.forEach(([queryKey, previousTasks]) => {
        queryClient.setQueryData(queryKey, previousTasks);
      });

      const errorMessage = getErrorMessage(err, "Failed to delete task");
      showToast("error", errorMessage || "Failed to delete task.", "Error");
    },
    onSuccess: () => {
      showToast("success", "Task deleted successfully.", "Success");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    },
  });

  const handleDelete = useCallback(
    async (taskId: number) => {
      await mutateTask({ taskId });
    },
    [mutateTask]
  );

  return { handleDelete, isPending };
};
